<script setup>
import throttle from 'lodash.throttle'
import { BREAKPOINTS } from '/utils/constants'

const props = defineProps({
  slides: {
    type: Array,
    default: null,
  },
})

const carouselRef = ref(null)
const carouselListRef = ref(null)
const innerWidth = ref(0)

const index = ref(0)
const formattedIndex = computed(() =>
  (index.value + 1).toString().padStart(2, 0)
)

const slidesCount = computed(() => props.slides?.length || 0)

const itemsPerPage = computed(() => {
  if (innerWidth.value < BREAKPOINTS.L) {
    return 1
  } else {
    return 3
  }
})
const pages = computed(() => Math.ceil(slidesCount.value / itemsPerPage.value))
const formattedPages = computed(() => pages.value.toString().padStart(2, 0))

// Handlers
function handlePrevious() {
  index.value = Math.max(index.value - 1, 0)
}

function handleNext() {
  index.value = Math.min(index.value + 1, pages.value - 1)
}

function handleScroll() {
  const slides = carouselRef.value.querySelectorAll('.carousel__slide')
  const slidesArray = [...slides]

  slidesArray.some((slide) => {
    const slideRect = slide.getBoundingClientRect()

    if (slideRect.x < innerWidth.value * 0.5) {
      index.value = slidesArray.indexOf(slide)
    }
  })
}

function handleResize() {
  index.value = 0
  innerWidth.value = window.innerWidth
}

// Lifecycle
const throttledHandleScroll = ref(null)
onMounted(() => {
  handleResize()
  window.addEventListener('resize', handleResize)

  throttledHandleScroll.value = throttle(handleScroll, 100)
  carouselListRef.value.addEventListener('scroll', throttledHandleScroll.value)
})

onBeforeUnmount(() => {
  carouselListRef.value.removeEventListener(
    'scroll',
    throttledHandleScroll.value
  )
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div ref="carouselRef" class="carousel">
    <ul
      ref="carouselListRef"
      class="carousel__list"
      :style="
        pages > 1 && {
          transform: `translate3D(
          calc(${index} * -100% ${index !== 0 ? '- var(--gap)' : ''}), 0, 0)`,
        }
      "
    >
      <li v-for="slide in slides" :key="slide.sys.id" class="carousel__slide">
        <CardDetails
          class="carousel__slide-item"
          :expand-button-gtm-class="slide.expandButtonGtmClass"
          :thumbnail="slide.thumbnail"
          :title="slide.title"
          :copy="slide.copy"
        />
      </li>
    </ul>

    <div class="carousel__navigation" v-if="pages > 1">
      <ButtonIcon
        class="carousel__button"
        icon="chevron-left"
        :is-open="true"
        @click="handlePrevious"
      />

      <p class="form__progress">
        <strong>{{ formattedIndex }}</strong> – {{ formattedPages }}
      </p>

      <ButtonIcon
        class="carousel__button"
        icon="chevron-right"
        :is-open="true"
        @click="handleNext"
      />
    </div>
  </div>
</template>

<style lang="scss">
// Vendors
@import 'styles/vendors/sass-mq/mq';

// Utils
@import 'styles/utils/variables';
@import 'styles/utils/functions';

.carousel {
  --gap: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @include mq($from: 'l') using($from) {
    --gap: #{gs(0, 1, $from)};
    margin: 0 auto;
    // max-width: 990px;
  }

  &__list {
    margin: 0 auto;
    // max-width: 990px;
    display: inline-flex;
    transition: transform 0.3s;
    will-change: transform;

    @include mq($until: 'l') using($until) {
      margin: 0;
      padding: 0 var(--grid-edge);
      max-width: unset;
      overflow-y: auto;
      scrollbar-width: none; /* Firefox */
      transform: none !important;
    }
  }

  &__list::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }

  &__slide {
    display: flex;
    justify-content: stretch;
    width: gs(6, 5, 'xxxs');
    flex-shrink: 0;

    @include mq($from: 'l') using($from) {
      width: gs(6, 5, 'l');
      // max-width: 314px;
      // max-height: 398px;
    }

    &-item {
      height: 100%;
    }
  }

  &__slide + &__slide {
    margin-left: var(--gap);
  }

  &__navigation {
    margin: 24px auto 0;
    display: flex;
    justify-content: center;
    align-items: center;

    @include mq($from: 'l') using($from) {
      justify-content: space-between;
      width: gs(6, 5, $from);
    }
  }

  &__button {
    @include mq($until: 'l') using($until) {
      display: none;
    }
  }

  &__button .icon {
    color: $brand-white;
    width: 14px;
    height: 14px;
  }
}
</style>
